import { Workbox } from 'workbox-window/Workbox';
import { exceptionLogger, log, logException } from '@paradigm/logging';

let attemptedRegistration = false;

export default function registerServiceWorker() {
  if (NODE_ENV !== 'production') {
    log(`Skipping service worker registration because NODE_ENV=${NODE_ENV}`);
    return null;
  }

  if (attemptedRegistration) {
    throw new Error('Must register service worker only once');
  }
  attemptedRegistration = true;

  if (!('serviceWorker' in navigator)) {
    log('Skipped service worker registration due to lack of support');
    return null;
  }

  const wb = initialize();
  wb.register()
    .then(() => handleRegistered(wb))
    .catch((error) => {
      logException('Failed service worker registration', error);
      unregisterServiceWorker().catch(
        exceptionLogger('Failed to unregister existing service worker'),
      );
    });
  return wb;
}

function initialize() {
  const wb = new Workbox('/service-worker.js');
  wb.addEventListener('activated', handleAvailableUpdate);
  return wb;
}

function handleAvailableUpdate() {
  log('Service worker update available');
  window.location.reload();
}

function handleRegistered(wb: Workbox) {
  log('Service worker registered');
  // Check for updates regularly with a random 60 seconds jitter
  setInterval(() => {
    // A failed update check is almost always transient — e.g.
    // `/service-worker.js` briefly 404s during a deploy or a CloudFront edge
    // blip. Just log it and let the next interval retry. Do NOT unregister:
    // unregistering does not evict the active SW from already-open tabs, so it
    // strands long-lived tabs on the old cached build with no further update
    // checks — which is what marooned users on stale versions.
    wb.update().catch(exceptionLogger('Failed service worker update check'));
  }, Math.floor(Math.random() * 60 * 1000) + 10 * 60 * 1000);
}

async function unregisterServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  log('Attempting to unregister existing service worker');
  const registrations = await navigator.serviceWorker.getRegistrations();
  for (const registration of registrations) {
    await registration.unregister();
  }
}
